// P1 OD-SNAPSHOT — the farmer's profile, one read shared by ProtectedRoute (SD-O68) and Settings: live when the radio
// is up, the saved copy (snapshot key `profile`) when it is not. A farmer with a snapshot is never bounced to login or
// left on a skeleton because the profile row could not be re-read.
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';
import { useSnapshotQuery, type SnapshotQueryResult } from '@/shared/hooks/snapshot/useSnapshotQuery';

/** The row Settings renders (profiles.* plus the country and crop joins); ProtectedRoute reads is_onboarded / country_id. */
export type ProfileRow = Record<string, unknown> & {
  id?: string;
  is_onboarded?: boolean | null;
  country_id?: string | null;
  full_name?: string | null;
  phone?: string | null;
  auth_phone?: string | null;
  region?: string | null;
  village?: string | null;
  farm_size_hectares?: number | null;
  avatar_url?: string | null;
  language?: string | null;
  countries?: { name?: string; code?: string } | null;
  crops?: { name?: string; icon?: string; code?: string; names?: unknown } | null;
};

export function useProfileSnapshot(opts: { enabled?: boolean } = {}): SnapshotQueryResult<ProfileRow> {
  const { user } = useAuth();
  const uid = user?.id;
  return useSnapshotQuery<ProfileRow>(
    'profile',
    async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*, countries(name, code), crops(name, icon, code, names)')
        .eq('id', uid as string)
        .maybeSingle();
      if (error) throw error;
      // No row is a real answer for a brand-new account; keep it well-formed so the gate can decide.
      return (data ?? { id: uid, is_onboarded: null, country_id: null }) as ProfileRow;
    },
    { farmerId: uid, enabled: opts.enabled ?? true, staleTime: 5 * 60 * 1000 },
  );
}
